/**
 * The clause text, with marks on it and a selection that means something.
 *
 * "Selection is the core interaction" is the design brief's phrase, and the
 * bar that appears is the single most important gesture in the product: it is
 * the whole distance between *this clause is a problem* and a claim carrying
 * the verbatim words. So it has to be immediate and it has to be precise.
 *
 * Precise means the offsets are computed against the clause string itself, not
 * against the DOM. Once marks are rendered the text is split across dozens of
 * spans, and `Range.startOffset` is an offset into whichever span the drag
 * happened to begin in. Walking the rendered segments back to a source index
 * is what keeps a mark pointing at the words the person actually chose, on the
 * second annotation as much as the first.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { segment, type Segment } from '@tmea/shared';
import type { PlacedAnnotation } from '../lib/api.js';

/** Where the action bar should sit, in coordinates local to the text block. */
interface BarPosition {
  top: number;
  left: number;
}

export interface Selection {
  start: number;
  end: number;
  exact: string;
}

export function Annotatable({
  text,
  annotations,
  activeId,
  onSelect,
  onOpenMark,
  children,
}: {
  text: string;
  annotations: readonly PlacedAnnotation[];
  activeId: string | null;
  /** Fires with a live selection, or null when it is cleared. */
  onSelect: (selection: Selection | null, at: BarPosition | null) => void;
  onOpenMark: (id: string) => void;
  /** The action bar, rendered by the parent so it can own the composer. */
  children?: React.ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [segments, setSegments] = useState<Segment[]>(() => segment(text, annotations));

  useLayoutEffect(() => {
    setSegments(segment(text, annotations));
  }, [text, annotations]);

  /**
   * Turns the browser's selection into offsets into `text`.
   *
   * Each rendered span carries its own start index, so the source offset is
   * that index plus the offset within the span. Reading `range.startOffset`
   * alone would be an offset into a fragment.
   */
  const readSelection = useCallback(() => {
    const dom = window.getSelection();
    if (!dom || dom.isCollapsed || dom.rangeCount === 0) {
      onSelect(null, null);
      return;
    }

    const range = dom.getRangeAt(0);
    const root = host.current;
    if (!root || !root.contains(range.commonAncestorContainer)) {
      onSelect(null, null);
      return;
    }

    const offsetIn = (node: Node, within: number): number | null => {
      const span = (node.nodeType === Node.TEXT_NODE ? node.parentElement : node) as HTMLElement;
      const base = span?.closest<HTMLElement>('[data-start]');
      if (!base) return null;
      return Number(base.dataset['start']) + within;
    };

    const from = offsetIn(range.startContainer, range.startOffset);
    const to = offsetIn(range.endContainer, range.endOffset);
    if (from === null || to === null) {
      onSelect(null, null);
      return;
    }

    const start = Math.min(from, to);
    const end = Math.max(from, to);
    if (end <= start) {
      onSelect(null, null);
      return;
    }

    /*
     * The bar's own position, in the block's coordinates, already flipped.
     *
     * This used to report the selection's top and let the bar draw itself at
     * `top - 44`. Select anything on the first line of a passage and that is
     * about −44: the bar renders above the block and is simply not on screen.
     * Which is why it appeared to work sometimes — it depended entirely on how
     * far down the passage you happened to drag.
     *
     * So the flip is decided here, where the geometry is known. Above the
     * selection when there is room, below it when there is not, and never
     * outside the block.
     *
     * The rect is the selection's first line rather than its bounding box. A
     * selection running to the end of one line and onto the next has a
     * bounding box the full width of the column, so its left edge is the
     * column's left edge — the bar detaches from the words and lands under the
     * margin numbers.
     */
    const rects = range.getClientRects();
    const rect = rects.length > 0 ? rects[0]! : range.getBoundingClientRect();
    const box = root.getBoundingClientRect();

    const BAR = 44;
    const above = rect.top - box.top - BAR;

    onSelect(
      { start, end, exact: text.slice(start, end) },
      {
        top: above >= 0 ? above : rect.bottom - box.top + 8,
        left: Math.max(0, rect.left - box.left),
      },
    );
  }, [onSelect, text]);

  /*
   * The bar goes away when the selection does.
   *
   * `onMouseUp` is bound to the text block, so it only ever ran when the
   * gesture ended *inside* the prose. Click anywhere else on the page — the
   * rail, the dock, the margin — and the browser drops the selection while
   * nothing tells React, so the bar sits over text that is no longer chosen
   * and its next press acts on a stale range.
   *
   * `selectionchange` is the only event that fires for every way a selection
   * can end, including Escape, a click in another pane, and a caret moved by
   * the keyboard. It fires constantly during a drag, so this reacts to the
   * collapse and nothing else; re-reading the selection here would fight the
   * mouseup handler for the same gesture.
   *
   * The action bar presses on mousedown with preventDefault precisely so the
   * selection survives long enough to be acted on, so this does not race it.
   */
  useEffect(() => {
    const onSelectionChange = (): void => {
      const dom = window.getSelection();
      if (!dom || dom.isCollapsed || dom.rangeCount === 0) onSelect(null, null);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [onSelect]);

  return (
    <div ref={host} className="relative" style={{ paddingLeft: 22 }}>
      <Gutter segments={segments} annotations={annotations} onOpenMark={onOpenMark} />
      <div
        className="register-reading measure"
        onMouseUp={readSelection}
        onKeyUp={(event) => {
          // Keyboard selection is a first-class path, not an afterthought:
          // shift+arrow is how anyone using a screen reader selects text.
          if (event.shiftKey || event.key === 'Shift') readSelection();
        }}
      >
        {segments.map((piece) => (
          <Piece
            key={piece.start}
            piece={piece}
            annotations={annotations}
            activeId={activeId}
            onOpenMark={onOpenMark}
          />
        ))}
      </div>
      {children}
    </div>
  );
}

/**
 * One run of text, styled by whatever marks cover it.
 *
 * Colour here is a person, which is the one thing saturated hue is reserved
 * for inside the reader canvas. Overlap deepens the wash rather than mixing
 * hues, because a blended colour would read as a seventh person.
 */
function Piece({
  piece,
  annotations,
  activeId,
  onOpenMark,
}: {
  piece: Segment;
  annotations: readonly PlacedAnnotation[];
  activeId: string | null;
  onOpenMark: (id: string) => void;
}) {
  if (piece.marks.length === 0) {
    return <span data-start={piece.start}>{piece.text}</span>;
  }

  /*
   * The loudest verb on this run, not whichever mark happens to be first.
   *
   * `piece.marks[0]` is arbitrary — it is document order. So a highlight laid
   * over a passage that already carried a question rendered as the question: a
   * dashed underline and no fill, which is exactly the "I highlighted it and
   * there is no colour" report. The mark was there and the wrong one was
   * speaking for it.
   *
   * Ordered by how much of the passage each verb claims. A claim is the
   * strongest thing anyone can do to these words — they are going to the
   * school — and a highlight is a deliberate act of marking the text itself,
   * so both outrank an annotation that merely hangs off it.
   */
  const VERB_RANK = ['claim', 'highlight', 'note', 'question', 'compare', 'ask'];
  const onPiece = piece.marks
    .map((id) => annotations.find((a) => a.id === id))
    .filter((a): a is PlacedAnnotation => a !== undefined);
  const top =
    [...onPiece].sort(
      (a, b) =>
        (VERB_RANK.indexOf(a.verb) < 0 ? 99 : VERB_RANK.indexOf(a.verb)) -
        (VERB_RANK.indexOf(b.verb) < 0 ? 99 : VERB_RANK.indexOf(b.verb)),
    )[0] ?? onPiece[0];
  const isActive = piece.marks.some((id) => id === activeId);
  const hue = top?.authorColour ? `var(--color-${top.authorColour})` : 'var(--color-muted)';

  /*
   * Shape says what was done; colour says who did it.
   *
   * Every verb used to render as the same nine-per-cent wash with the same
   * underline, which answered neither question. At that alpha nothing looked
   * marked at all — on screen a highlight read as a faint box around the line,
   * which says "container", not "someone put a pen through this" — and a
   * highlight, a note and a claim were indistinguishable from one another.
   *
   * Hue stays the person, because that rule does not bend inside the reader
   * canvas. The shape is free, so the verb takes it: a highlighter band, a
   * note's tab, a claim's rule down the side.
   */
  const verb = top?.verb ?? 'highlight';
  const classes = [
    'mark',
    `mark-${verb}`,
    piece.marks.length > 1 ? 'mark-stacked' : '',
    isActive ? 'mark-active' : '',
    top?.visibility === 'private' ? 'mark-private' : '',
    top?.resolvedAt ? 'mark-resolved' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * The corner of the post-it, on the last run the note covers.
   *
   * Somebody wrote words about this passage and the text said so only by being
   * faintly tinted — which is indistinguishable from a highlight, and a
   * highlight means the opposite thing: marked, but nothing said. The tab is
   * what tells a reader there is something to read, and it is what makes the
   * post-it toggle legible when the post-its are switched off: the text still
   * shows where they hang.
   *
   * Once per mark, on the piece where the mark ends, because a note spanning
   * three overlapping runs would otherwise sprout three tabs.
   */
  const endsHere = top !== undefined && top.end === piece.start + piece.text.length;
  const carriesWords = Boolean(top?.body?.trim());

  return (
    <span
      data-start={piece.start}
      role="button"
      tabIndex={0}
      onClick={() => onOpenMark(piece.marks[0]!)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpenMark(piece.marks[0]!);
      }}
      title={describe(annotations, piece.marks)}
      className={classes}
      style={{ '--mark-hue': hue } as CSSProperties}
    >
      {piece.text}
      {endsHere && carriesWords ? <span className="mark-tab" aria-hidden /> : null}
    </span>
  );
}

/**
 * The margin gutter: who has marked this clause.
 *
 * Initials rather than avatars, because there are no photographs in this
 * product and there never will be — a face is an identification, and the
 * threat model is an employer. Colour carries the recognition; the initials
 * are the pairing that keeps it legible to anyone who cannot rely on hue.
 *
 * An anonymous mark shows a hollow ring with no letters. It has to appear, or
 * the gutter would silently under-report how contested a clause is; it must
 * not be attributable, which is the whole point of choosing anonymous.
 */
function Gutter({
  segments,
  annotations,
  onOpenMark,
}: {
  segments: readonly Segment[];
  annotations: readonly PlacedAnnotation[];
  onOpenMark: (id: string) => void;
}) {
  // One entry per person per clause, not per mark: six highlights from one
  // person is one presence in the margin, not a stack of six.
  const seen = new Map<string, PlacedAnnotation>();
  for (const piece of segments) {
    for (const id of piece.marks) {
      const mark = annotations.find((a) => a.id === id);
      if (!mark) continue;
      const key = mark.isAnonymous ? `anon:${mark.id}` : (mark.authorName ?? mark.id);
      if (!seen.has(key)) seen.set(key, mark);
    }
  }

  if (seen.size === 0) return null;

  return (
    <div
      className="absolute left-0 top-0 flex flex-col gap-1"
      aria-label="Who has marked this clause"
    >
      {[...seen.values()].map((mark) => {
        const hue = mark.authorColour ? `var(--color-${mark.authorColour})` : 'var(--color-muted)';
        return (
          <button
            key={mark.id}
            onClick={() => onOpenMark(mark.id)}
            title={mark.isAnonymous ? 'Anonymous' : (mark.authorName ?? 'Someone')}
            className="flex h-4 w-4 items-center justify-center rounded-full text-[8px]"
            style={{
              border: `1px solid ${hue}`,
              color: hue,
              background: mark.isAnonymous
                ? 'transparent'
                : `color-mix(in oklab, ${hue} 12%, transparent)`,
            }}
          >
            {mark.isAnonymous ? '' : (mark.authorInitials ?? '?')}
          </button>
        );
      })}
    </div>
  );
}

function describe(annotations: readonly PlacedAnnotation[], ids: readonly string[]): string {
  const names = ids
    .map((id) => annotations.find((a) => a.id === id))
    .filter(Boolean)
    .map((a) => `${a!.isAnonymous ? 'Anonymous' : (a!.authorName ?? 'Someone')} · ${a!.verb}`);
  return names.join('\n');
}
